/* ==========================================================================
   AUREN — theme.js
   Core runtime. Zero dependencies. Progressive enhancement only:
   every component works (degraded) without JS.
   Pattern: Custom Elements + a tiny shared utility layer.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1. Utilities
   * ------------------------------------------------------------------ */
  const U = (window.AURENUtils = {
    qs: (sel, scope = document) => scope.querySelector(sel),
    qsa: (sel, scope = document) => Array.from(scope.querySelectorAll(sel)),

    debounce(fn, wait = 200) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    throttle(fn, limit = 100) {
      let waiting = false;
      return function (...args) {
        if (waiting) return;
        fn.apply(this, args);
        waiting = true;
        setTimeout(() => (waiting = false), limit);
      };
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    formatMoney(cents, format) {
      const fmt = format || (window.AUREN && window.AUREN.shop.moneyFormat) || '${{amount}}';
      const value = Number(cents || 0);
      function fixed(number, precision = 2) {
        const n = (number / 100).toFixed(precision);
        return n.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      }
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
        switch (name) {
          case 'amount': return fixed(value, 2);
          case 'amount_no_decimals': return fixed(value, 0);
          case 'amount_with_comma_separator': return fixed(value, 2).replace(/,/g, '·').replace(/\./g, ',').replace(/·/g, '.');
          case 'amount_no_decimals_with_comma_separator': return fixed(value, 0).replace(/,/g, '.');
          default: return fixed(value, 2);
        }
      });
    },

    announce(message) {
      const region = document.getElementById('a11y-live');
      if (!region) return;
      region.textContent = '';
      window.setTimeout(() => (region.textContent = message), 60);
    },

    lockScroll(lock) {
      const body = document.body;
      if (lock) {
        U._scrollY = window.scrollY;
        body.style.top = `-${U._scrollY}px`;
        body.classList.add('is-locked');
        body.style.position = 'fixed';
        body.style.width = '100%';
      } else {
        body.classList.remove('is-locked');
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        window.scrollTo(0, U._scrollY || 0);
      }
    },

    trapFocus(container, initial) {
      const selectors =
        'a[href], button:not([disabled]), textarea, input:not([type="hidden"]):not([disabled]), select, details, [tabindex]:not([tabindex="-1"])';
      const focusables = U.qsa(selectors, container).filter((el) => el.offsetParent !== null);
      const first = initial || focusables[0];
      const last = focusables[focusables.length - 1];
      if (first) first.focus({ preventScroll: true });

      function onKeydown(e) {
        if (e.key !== 'Tab' || focusables.length === 0) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      container.addEventListener('keydown', onKeydown);
      return () => container.removeEventListener('keydown', onKeydown);
    },

    async fetchJSON(url, options = {}) {
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...options
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Request failed'), { data });
      return data;
    },

    async fetchSection(url) {
      const res = await fetch(url);
      const text = await res.text();
      return new DOMParser().parseFromString(text, 'text/html');
    },

    toast(message, variant = '') {
      document.dispatchEvent(new CustomEvent('auren:toast', { detail: { message, variant } }));
    },

    emit(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    }
  });

  /* ------------------------------------------------------------------ *
   * 2. Scroll reveal + parallax (Framer-grade feel, IntersectionObserver)
   * ------------------------------------------------------------------ */
  function initReveal() {
    const nodes = U.qsa('[data-reveal]');
    if (!nodes.length) return;
    if (U.prefersReducedMotion() || document.body.dataset.motion === 'false') {
      nodes.forEach((n) => n.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const group = el.closest('[data-reveal-group]');
          if (group) {
            const siblings = U.qsa('[data-reveal]', group);
            const index = siblings.indexOf(el);
            el.style.setProperty('--reveal-delay', `${Math.min(index, 8) * 70}ms`);
          }
          el.classList.add('is-revealed');
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    nodes.forEach((n) => io.observe(n));
  }

  function initParallax() {
    const nodes = U.qsa('[data-parallax]');
    if (!nodes.length || U.prefersReducedMotion()) return;
    let ticking = false;
    function update() {
      const vh = window.innerHeight;
      nodes.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const speed = parseFloat(el.dataset.parallax) || 0.15;
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        el.style.transform = `translate3d(0, ${(progress * speed * 100).toFixed(2)}px, 0)`;
      });
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ------------------------------------------------------------------ *
   * 3. Sticky header
   * ------------------------------------------------------------------ */
  class AurenHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = 0;
      this.threshold = 12;
      this.autoHide = this.dataset.autoHide === 'true';
      this.onScroll = U.throttle(this.handleScroll.bind(this), 60);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.handleScroll();
      this.initDesktopMenus();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    handleScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > this.threshold);
      if (this.classList.contains('is-transparent') && y > 40) this.classList.remove('is-transparent');
      if (this.autoHide && !this.querySelector('.nav__item.is-open')) {
        const goingDown = y > this.lastY && y > 240;
        this.classList.toggle('is-hidden', goingDown);
      }
      this.lastY = y;
    }

    initDesktopMenus() {
      const items = U.qsa('.nav__item--has-mega', this);
      items.forEach((item) => {
        const trigger = U.qs('.nav__link', item);
        let closeTimer;
        const open = () => {
          clearTimeout(closeTimer);
          items.forEach((i) => i !== item && i.classList.remove('is-open'));
          item.classList.add('is-open');
          if (trigger) trigger.setAttribute('aria-expanded', 'true');
        };
        const close = () => {
          closeTimer = setTimeout(() => {
            item.classList.remove('is-open');
            if (trigger) trigger.setAttribute('aria-expanded', 'false');
          }, 120);
        };
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);
        item.addEventListener('focusin', open);
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) close();
        });
        if (trigger) {
          trigger.addEventListener('click', (e) => {
            if (window.matchMedia('(min-width: 990px)').matches && !item.classList.contains('is-open')) {
              e.preventDefault();
              open();
            }
          });
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') items.forEach((i) => i.classList.remove('is-open'));
      });
    }
  }
  customElements.define('auren-header', AurenHeader);

  /* ------------------------------------------------------------------ *
   * 4. Drawer (cart, menu, filters, search)
   * ------------------------------------------------------------------ */
  class AurenDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = U.qs('.drawer__overlay', this);
      this.panel = U.qs('.drawer__panel', this);
      this.releaseFocus = null;
      this.opener = null;

      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      U.qsa('[data-drawer-close]', this).forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      document.addEventListener('click', (e) => {
        const toggle = e.target.closest(`[data-drawer-open="${this.id}"]`);
        if (!toggle) return;
        e.preventDefault();
        this.open(toggle);
      });

      document.addEventListener(`auren:open:${this.id}`, () => this.open());
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      U.lockScroll(true);
      window.setTimeout(() => {
        this.releaseFocus = U.trapFocus(this.panel || this);
      }, 60);
      U.emit('auren:drawer:open', { id: this.id });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      U.lockScroll(false);
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
      U.emit('auren:drawer:close', { id: this.id });
    }
  }
  customElements.define('auren-drawer', AurenDrawer);

  /* ------------------------------------------------------------------ *
   * 5. Modal
   * ------------------------------------------------------------------ */
  class AurenModal extends HTMLElement {
    connectedCallback() {
      this.panel = U.qs('.modal__panel', this);
      U.qsa('[data-modal-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      const overlay = U.qs('.modal__overlay', this);
      if (overlay) overlay.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
      document.addEventListener('click', (e) => {
        const t = e.target.closest(`[data-modal-open="${this.id}"]`);
        if (!t) return;
        e.preventDefault();
        this.open(t);
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      U.lockScroll(true);
      this.release = U.trapFocus(this.panel || this);
    }
    close() {
      this.classList.remove('is-open');
      U.lockScroll(false);
      if (this.release) this.release();
      if (this.opener) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('auren-modal', AurenModal);

  /* ------------------------------------------------------------------ *
   * 6. Accordion (height-animated, a11y complete)
   * ------------------------------------------------------------------ */
  class AurenAccordion extends HTMLElement {
    connectedCallback() {
      this.single = this.dataset.single === 'true';
      this.triggers = U.qsa('.accordion__trigger', this);
      this.triggers.forEach((trigger) => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;
        if (trigger.getAttribute('aria-expanded') === 'true') {
          panel.style.height = 'auto';
        }
        trigger.addEventListener('click', () => this.toggle(trigger, panel));
      });
    }

    toggle(trigger, panel) {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      if (this.single && !expanded) {
        this.triggers.forEach((t) => {
          if (t === trigger) return;
          const p = document.getElementById(t.getAttribute('aria-controls'));
          if (p && t.getAttribute('aria-expanded') === 'true') this.collapse(t, p);
        });
      }
      expanded ? this.collapse(trigger, panel) : this.expand(trigger, panel);
    }

    expand(trigger, panel) {
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      const inner = panel.firstElementChild;
      panel.style.height = `${inner.offsetHeight}px`;
      panel.addEventListener('transitionend', function done() {
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', done);
      });
    }

    collapse(trigger, panel) {
      trigger.setAttribute('aria-expanded', 'false');
      const inner = panel.firstElementChild;
      panel.style.height = `${inner.offsetHeight}px`;
      requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
    }
  }
  customElements.define('auren-accordion', AurenAccordion);

  /* ------------------------------------------------------------------ *
   * 7. Tabs (roving tabindex, arrow keys — WAI-ARIA APG)
   * ------------------------------------------------------------------ */
  class AurenTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = U.qsa('[role="tab"]', this);
      this.panels = U.qsa('[role="tabpanel"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const map = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
          if (!(e.key in map)) return;
          e.preventDefault();
          let next;
          if (map[e.key] === 'first') next = 0;
          else if (map[e.key] === 'last') next = this.tabs.length - 1;
          else next = (i + map[e.key] + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel, i) => (panel.hidden = i !== index));
    }
  }
  customElements.define('auren-tabs', AurenTabs);

  /* ------------------------------------------------------------------ *
   * 8. Carousel
   * ------------------------------------------------------------------ */
  class AurenCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = U.qs('.carousel__viewport', this);
      this.slides = U.qsa('.carousel__slide', this);
      this.prevBtn = U.qs('[data-carousel-prev]', this);
      this.nextBtn = U.qs('[data-carousel-next]', this);
      this.dots = U.qsa('.carousel__dot', this);
      if (!this.viewport) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
      this.dots.forEach((dot, i) =>
        dot.addEventListener('click', () => {
          if (this.slides[i]) this.viewport.scrollTo({ left: this.slides[i].offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
        })
      );
      this.viewport.addEventListener('scroll', U.throttle(() => this.updateState(), 100), { passive: true });
      this.updateState();
    }

    scrollByPage(dir) {
      const step = this.slides[0] ? this.slides[0].offsetWidth + 24 : this.viewport.clientWidth * 0.8;
      this.viewport.scrollBy({ left: step * dir, behavior: 'smooth' });
    }

    updateState() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      if (this.prevBtn) this.prevBtn.disabled = scrollLeft < 8;
      if (this.nextBtn) this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 8;
      if (this.dots.length) {
        const active = Math.round((scrollLeft / (scrollWidth - clientWidth || 1)) * (this.dots.length - 1));
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === active));
      }
    }
  }
  customElements.define('auren-carousel', AurenCarousel);

  /* ------------------------------------------------------------------ *
   * 9. Announcement rotator
   * ------------------------------------------------------------------ */
  class AurenAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = U.qsa('.announcement__slide', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      const speed = parseInt(this.dataset.speed || '5200', 10);
      this.timer = setInterval(() => this.next(), speed);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
    }
    next() {
      this.slides[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('auren-announcement', AurenAnnouncement);

  /* ------------------------------------------------------------------ *
   * 10. Toasts
   * ------------------------------------------------------------------ */
  class AurenToastHost extends HTMLElement {
    connectedCallback() {
      document.addEventListener('auren:toast', (e) => this.push(e.detail));
    }
    push({ message, variant = '', duration = 4200 }) {
      const el = document.createElement('div');
      el.className = `toast ${variant ? `toast--${variant}` : ''}`;
      el.setAttribute('role', 'status');
      el.innerHTML = `<span>${message}</span><button class="toast__close" aria-label="Dismiss">&times;</button>`;
      el.querySelector('.toast__close').addEventListener('click', () => this.dismiss(el));
      this.appendChild(el);
      U.announce(message);
      setTimeout(() => this.dismiss(el), duration);
    }
    dismiss(el) {
      if (!el.isConnected) return;
      el.classList.add('is-leaving');
      setTimeout(() => el.remove(), 220);
    }
  }
  customElements.define('auren-toast-host', AurenToastHost);

  /* ------------------------------------------------------------------ *
   * 11. Quantity stepper
   * ------------------------------------------------------------------ */
  class AurenQuantity extends HTMLElement {
    connectedCallback() {
      this.input = U.qs('input', this);
      this.minus = U.qs('[data-qty-minus]', this);
      this.plus = U.qs('[data-qty-plus]', this);
      if (!this.input) return;
      if (this.minus) this.minus.addEventListener('click', () => this.step(-1));
      if (this.plus) this.plus.addEventListener('click', () => this.step(1));
      this.input.addEventListener('change', () => this.validate());
      this.validate();
    }
    step(dir) {
      const min = parseInt(this.input.min || '1', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const value = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + dir));
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.validate();
    }
    validate() {
      const min = parseInt(this.input.min || '1', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const value = parseInt(this.input.value, 10) || min;
      if (this.minus) this.minus.disabled = value <= min;
      if (this.plus) this.plus.disabled = value >= max;
    }
  }
  customElements.define('auren-quantity', AurenQuantity);

  /* ------------------------------------------------------------------ *
   * 12. Mobile menu disclosure
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-menu-toggle]');
    if (!trigger) return;
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    const open = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!open));
    panel.classList.toggle('is-open', !open);
  });

  /* ------------------------------------------------------------------ *
   * 13. Predictive search
   * ------------------------------------------------------------------ */
  class AurenPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = U.qs('input[type="search"]', this);
      this.results = U.qs('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener(
        'input',
        U.debounce(() => this.search(), 260)
      );
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.results.innerHTML = '<div class="stack-sm"><div class="skeleton skeleton--text skeleton--w-80"></div><div class="skeleton skeleton--text skeleton--w-60"></div><div class="skeleton skeleton--text skeleton--w-40"></div></div>';
      try {
        const url = `${window.AUREN.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('[data-predictive-inner]');
        this.results.innerHTML = inner ? inner.innerHTML : '';
      } catch (err) {
        this.results.innerHTML = '';
      }
    }
  }
  customElements.define('auren-predictive-search', AurenPredictiveSearch);

  /* ------------------------------------------------------------------ *
   * 14. Wishlist (client-side, no app required)
   * ------------------------------------------------------------------ */
  const Wishlist = (window.AURENWishlist = {
    key: 'auren:wishlist',
    read() {
      try {
        return JSON.parse(localStorage.getItem(this.key) || '[]');
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem(this.key, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable — fail silently */
      }
      this.paint();
      U.emit('auren:wishlist:change', { list });
    },
    has(handle) {
      return this.read().includes(handle);
    },
    toggle(handle) {
      const list = this.read();
      const i = list.indexOf(handle);
      i === -1 ? list.push(handle) : list.splice(i, 1);
      this.write(list);
      return i === -1;
    },
    paint() {
      const list = this.read();
      U.qsa('[data-wishlist-toggle]').forEach((btn) => {
        const on = list.includes(btn.dataset.wishlistToggle);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', String(on));
      });
      U.qsa('[data-wishlist-count]').forEach((el) => {
        el.textContent = list.length;
        el.dataset.count = list.length;
      });
    }
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const added = Wishlist.toggle(btn.dataset.wishlistToggle);
    U.toast(added ? 'Saved to your wishlist.' : 'Removed from your wishlist.');
  });

  /* ------------------------------------------------------------------ *
   * 15. Reading progress bar
   * ------------------------------------------------------------------ */
  function initProgressBar() {
    const bar = U.qs('[data-progress-bar]');
    if (!bar) return;
    const onScroll = U.throttle(() => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = `${Math.min(100, (window.scrollY / (h || 1)) * 100)}%`;
    }, 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------ *
   * 16. Video facade (click-to-load — protects LCP & CLS)
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-video-play]');
    if (!trigger) return;
    const wrap = trigger.closest('[data-video-wrap]');
    const src = trigger.dataset.videoPlay;
    if (!wrap || !src) return;
    const iframe = document.createElement('iframe');
    iframe.src = src.includes('?') ? `${src}&autoplay=1` : `${src}?autoplay=1`;
    iframe.title = trigger.dataset.videoTitle || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.loading = 'lazy';
    iframe.className = 'aspect-video full';
    iframe.style.border = '0';
    wrap.innerHTML = '';
    wrap.appendChild(iframe);
  });

  /* ------------------------------------------------------------------ *
   * 17. Copy to clipboard (share)
   * ------------------------------------------------------------------ */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    try {
      await navigator.clipboard.writeText(btn.dataset.copy);
      U.toast('Link copied.', 'success');
    } catch (err) {
      U.toast('Could not copy the link.', 'error');
    }
  });

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot() {
    initReveal();
    initParallax();
    initProgressBar();
    Wishlist.paint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init after Shopify section rendering in the theme editor
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('auren:section:update', boot);
})();
